import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";
import { CalendarDays, Clock, ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Step mapping:
// 0 = Service, 1 = Date & Time, 2 = Staff (if exists), 3 = Details, 4 = Success
// Without staff: 0 → 1 → 3 → 4

export default function BookingWidget() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();

  const getServiceName = (svc) => {
    const lang = i18n.language;
    const tr = svc.translations?.[lang];
    return (tr?.name && tr.name.trim()) ? tr.name : svc.name;
  };
  const getServiceDesc = (svc) => {
    const lang = i18n.language;
    const tr = svc.translations?.[lang];
    return (tr?.description && tr.description.trim()) ? tr.description : svc.description;
  };

  const [ownerId, setOwnerId] = useState(null);
  const [resolveError, setResolveError] = useState(false);

  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const hasStaff = staffList.length > 0;
  const totalDuration = selectedServices.reduce((a, s) => a + s.duration, 0);
  const totalPrice = selectedServices.reduce((a, s) => a + s.price, 0);
  const serviceIdsParam = selectedServices.map((s) => s.id).join(",");

  const toggleService = (svc) => {
    setSelectedServices((prev) =>
      prev.find((s) => s.id === svc.id) ? prev.filter((s) => s.id !== svc.id) : [...prev, svc]
    );
  };

  const serviceNames = selectedServices.map((s) => getServiceName(s)).join(" + ");

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/widget/resolve/${slug}`)
      .then((r) => setOwnerId(r.data.owner_id))
      .catch(() => setResolveError(true));
  }, [slug]);

  useEffect(() => {
    if (!ownerId) return;
    axios.get(`${API}/widget/services`, { params: { owner_id: ownerId } }).then((r) => setServices(r.data));
    axios.get(`${API}/widget/settings`, { params: { owner_id: ownerId } }).then((r) => setSettings(r.data));
  }, [ownerId]);

  // Reload staff list with date + service filter
  useEffect(() => {
    if (!ownerId) return;
    const params = { owner_id: ownerId };
    if (selectedDate) params.date = format(selectedDate, "yyyy-MM-dd");
    if (serviceIdsParam) params.service_ids = serviceIdsParam;
    axios.get(`${API}/widget/staff`, { params }).then((r) => setStaffList(r.data));
  }, [ownerId, selectedDate, serviceIdsParam]);

  // Reload slots when services or date changes
  useEffect(() => {
    if (!selectedServices.length || !selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    const params = { service_ids: serviceIdsParam, date: format(selectedDate, "yyyy-MM-dd") };
    axios.get(`${API}/widget/slots`, { params })
      .then((r) => setSlots(r.data))
      .finally(() => setLoadingSlots(false));
  }, [serviceIdsParam, selectedDate]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (value) => {
    if (!value) { setEmailError(""); return false; }
    const valid = EMAIL_RE.test(value);
    setEmailError(valid ? "" : t("booking_email_invalid"));
    return valid;
  };

  const [gdprConsent, setGdprConsent] = useState(false);

  const handleBook = async () => {
    if (!form.name || !form.email) return;
    if (!validateEmail(form.email)) return;
    if (!gdprConsent) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/widget/book`, {
        service_ids: selectedServices.map((s) => s.id),
        staff_id: selectedStaff?.id || null,
        date: format(selectedDate, "yyyy-MM-dd"),
        start_time: selectedSlot,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        notes: form.notes,
      });
      setBooking(res.data);
      setStep(4);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("booking_failed");
      alert(msg);
    } finally { setSubmitting(false); }
  };

  const goAfterDateTime = () => setStep(hasStaff ? 2 : 3);
  const goBackFromStaff = () => setStep(1);
  const goBackFromDetails = () => setStep(hasStaff ? 2 : 1);

  const pc = settings?.widget_config?.primary_color || "#6366f1";

  // Progress bar labels
  const steps = hasStaff
    ? [t("booking_step_service"), t("booking_step_datetime"), t("booking_step_staff"), t("booking_step_details")]
    : [t("booking_step_service"), t("booking_step_datetime"), t("booking_step_details")];

  // Map visual step index (0,1,2,3) to actual step value
  const visualStep = hasStaff
    ? step === 3 ? 3 : step  // 0,1,2,3,4
    : step === 3 ? 2 : step === 1 ? 1 : step; // compress: 0→0, 1→1, 3→2

  if (resolveError) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-slate-400 text-sm">{t("booking_not_found")}</p>
          <p className="text-slate-500 text-xs mt-1">{t("booking_not_found_sub")}</p>
        </div>
      </div>
    );
  }

  if (!ownerId) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4">
        <Loader2 className="animate-spin text-indigo-400" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center p-4" data-testid="booking-widget">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6 relative">
          <div className="absolute top-0 right-0">
            <LanguageSwitcher variant="widget" />
          </div>
          <div className="w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${pc}20`, border: `1px solid ${pc}40` }}>
            <CalendarDays className="w-6 h-6" style={{ color: pc }} />
          </div>
          <h1 className="text-xl font-bold text-white font-heading">{settings?.business_name || t("booking_book_appointment")}</h1>
          {settings?.business_description && <p className="text-sm text-slate-400 mt-1">{settings.business_description}</p>}
        </div>

        {/* Progress */}
        {step < 4 && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {steps.map((label, i) => (
              <div key={label} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${visualStep >= i ? "text-white" : "text-slate-500 border border-slate-700"}`}
                  style={visualStep >= i ? { backgroundColor: pc } : {}}>
                  {visualStep > i ? <Check size={14} /> : i + 1}
                </div>
                {i < steps.length - 1 && <div className={`w-8 h-0.5 ${visualStep > i ? "bg-indigo-500" : "bg-slate-700"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div className="bg-slate-950/70 backdrop-blur-xl border border-white/10 rounded-xl p-6">

          {/* Step 0: Service multi-select */}
          {step === 0 && (
            <div className="space-y-3" data-testid="step-service">
              <h2 className="text-base font-semibold text-white mb-1">{t("booking_choose_service")}</h2>
              <p className="text-xs text-slate-500 mb-3">{t("booking_select_hint")}</p>
              {services.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">{t("booking_no_services")}</p>
              ) : (
                <>
                  {services.map((svc) => {
                    const sel = !!selectedServices.find((s) => s.id === svc.id);
                    return (
                      <button key={svc.id}
                        onClick={() => toggleService(svc)}
                        className={`w-full text-left p-4 rounded-lg border transition-colors group ${
                          sel
                            ? "border-indigo-500/60 bg-indigo-500/10"
                            : "border-slate-800 hover:border-indigo-500/40 bg-slate-900/50 hover:bg-slate-800/50"
                        }`}
                        data-testid={`widget-service-${svc.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel ? "border-indigo-500 bg-indigo-500" : "border-slate-600"}`}>
                              {sel && <Check size={10} className="text-white" />}
                            </div>
                            <div>
                              <div className={`text-sm font-medium transition-colors ${sel ? "text-indigo-300" : "text-white group-hover:text-indigo-300"}`}>{getServiceName(svc)}</div>
                              {svc.description && <div className="text-xs text-slate-400 mt-0.5">{getServiceDesc(svc)}</div>}
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-4">
                            <div className="text-sm font-mono text-white">CHF {svc.price.toFixed(2)}</div>
                            <div className="text-xs text-slate-500 flex items-center gap-1 justify-end"><Clock size={10} />{svc.duration}min</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {selectedServices.length > 0 && (
                    <div className="mt-2">
                      <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2 text-xs text-indigo-300 flex justify-between mb-3">
                        <span>{selectedServices.length} service{selectedServices.length > 1 ? "s" : ""} · {totalDuration} min</span>
                        <span className="font-mono">CHF {totalPrice.toFixed(2)}</span>
                      </div>
                      <Button onClick={() => setStep(1)} className="w-full text-white" style={{ backgroundColor: pc }}>
                        {t("booking_continue")} <ArrowRight size={14} className="ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 1: Date & Time */}
          {step === 1 && (
            <div data-testid="step-datetime">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={() => setStep(0)}>
                  <ArrowLeft size={16} />
                </Button>
                <h2 className="text-base font-semibold text-white">{t("booking_pick_datetime")}</h2>
              </div>
              <div className="flex justify-center mb-4">
                <Calendar mode="single" selected={selectedDate}
                  onSelect={(d) => { setSelectedDate(d); setSelectedStaff(null); }}
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                  className="rounded-lg border border-slate-800 bg-slate-900/50" />
              </div>
              {selectedDate && (
                <div className="mt-4">
                  <p className="text-sm text-slate-400 mb-2">{t("booking_available_times", { date: format(selectedDate, "MMM d, yyyy") })}</p>
                  {loadingSlots ? (
                    <div className="flex justify-center py-4"><Loader2 className="animate-spin text-slate-500" size={20} /></div>
                  ) : slots.length === 0 ? (
                    <p className="text-xs text-slate-500 text-center py-3">{t("booking_no_slots")}</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button key={s.time} disabled={!s.available}
                          onClick={() => setSelectedSlot(s.time)}
                          className={`py-2 px-1 rounded-lg text-xs font-mono transition-all border ${
                            selectedSlot === s.time
                              ? "text-white border-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.3)]"
                              : s.available
                                ? "text-slate-300 border-slate-700 hover:border-indigo-500/40"
                                : "text-slate-600 border-slate-800 cursor-not-allowed opacity-40"
                          }`}
                          style={selectedSlot === s.time ? { backgroundColor: pc } : {}}>
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedSlot && (
                    <Button onClick={goAfterDateTime} className="w-full mt-4 text-white" style={{ backgroundColor: pc }}>
                      {t("booking_continue")} <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Staff (only if staff exists) */}
          {step === 2 && hasStaff && (
            <div className="space-y-3" data-testid="step-staff">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={goBackFromStaff}>
                  <ArrowLeft size={16} />
                </Button>
                <h2 className="text-base font-semibold text-white">{t("booking_choose_staff")}</h2>
              </div>
              {/* Summary so far */}
              <div className="bg-slate-800/40 rounded-lg px-3 py-2 mb-2 text-xs text-slate-400 border border-slate-700/30">
                {format(selectedDate, "EEE, MMM d")} · {selectedSlot} · {serviceNames} · {totalDuration}min
              </div>
              <button onClick={() => { setSelectedStaff({ id: "any" }); setStep(3); }}
                className="w-full text-left p-4 rounded-lg border border-slate-700 hover:border-indigo-500/40 transition-colors bg-slate-900/50 hover:bg-slate-800/50 group"
                data-testid="staff-any">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-slate-300 text-sm">?</div>
                  <div>
                    <div className="text-sm font-medium text-white group-hover:text-indigo-300">{t("booking_any_staff")}</div>
                    <div className="text-xs text-slate-400">{t("booking_any_staff_sub")}</div>
                  </div>
                </div>
              </button>
              {staffList.map((s) => (
                <button key={s.id}
                  onClick={() => { if (!s.is_blocked) { setSelectedStaff(s); setStep(3); } }}
                  disabled={s.is_blocked}
                  className={`w-full text-left p-4 rounded-lg border transition-colors group ${
                    s.is_blocked
                      ? "border-slate-800 bg-slate-900/30 opacity-50 cursor-not-allowed"
                      : "border-slate-800 hover:border-indigo-500/40 bg-slate-900/50 hover:bg-slate-800/50"
                  }`}
                  data-testid={`staff-${s.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 font-semibold text-sm">
                        {s.name.charAt(0).toUpperCase()}
                      </div>
                      <div className={`text-sm font-medium ${s.is_blocked ? "text-slate-500" : "text-white group-hover:text-indigo-300"}`}>{s.name}</div>
                    </div>
                    {s.is_blocked
                      ? <span className="text-xs text-slate-500 shrink-0">{t("booking_unavailable")}</span>
                      : <span className="text-xs text-green-500 shrink-0">{t("booking_available")}</span>
                    }
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Customer Details */}
          {step === 3 && (
            <div data-testid="step-details">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400" onClick={goBackFromDetails}>
                  <ArrowLeft size={16} />
                </Button>
                <h2 className="text-base font-semibold text-white">{t("booking_your_details")}</h2>
              </div>
              <div className="bg-slate-800/40 rounded-lg p-3 mb-4 text-sm border border-slate-700/30">
                <div className="flex justify-between text-slate-300">
                  <span>{serviceNames}</span>
                  <span className="font-mono">CHF {totalPrice.toFixed(2)}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {selectedDate && format(selectedDate, "EEEE, MMM d, yyyy")} at {selectedSlot} · {totalDuration}min
                  {selectedStaff && selectedStaff.id !== "any" && ` · ${selectedStaff.name}`}
                </div>
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{t("booking_name")}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("booking_name_placeholder")} className="bg-slate-900/50 border-slate-700" data-testid="widget-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{t("booking_email")}</Label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => { setForm({ ...form, email: e.target.value }); validateEmail(e.target.value); }}
                    placeholder={t("booking_email_placeholder")}
                    className={`bg-slate-900/50 border-slate-700 ${emailError ? "border-red-500" : ""}`}
                    data-testid="widget-email-input"
                  />
                  {emailError && <p className="text-xs text-red-400">{emailError}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{t("booking_phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder={t("booking_phone_placeholder")} className="bg-slate-900/50 border-slate-700" data-testid="widget-phone-input" />
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                  <input
                    type="checkbox"
                    id="gdpr-consent"
                    checked={gdprConsent}
                    onChange={(e) => setGdprConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-600 accent-indigo-500 cursor-pointer flex-shrink-0"
                    data-testid="gdpr-checkbox"
                  />
                  <label htmlFor="gdpr-consent" className="text-xs text-slate-400 leading-relaxed cursor-pointer">
                    {t("booking_gdpr_text")}
                  </label>
                </div>
                <Button onClick={handleBook} disabled={submitting || !form.name || !form.email || !gdprConsent}
                  className="w-full mt-2 text-white disabled:opacity-40" style={{ backgroundColor: pc }} data-testid="confirm-booking-button">
                  {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                  {submitting ? t("booking_confirming") : t("booking_confirm")}
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {step === 4 && booking && (
            <div className="text-center py-4" data-testid="booking-success">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center bg-indigo-500/20">
                <Check size={28} style={{ color: pc }} />
              </div>
              <h2 className="text-lg font-bold text-white mb-1">{t("booking_pending_title")}</h2>
              <p className="text-sm text-slate-400 mb-4">{t("booking_pending_sub")}</p>
              <div className="bg-slate-800/40 rounded-lg p-4 text-left text-sm border border-slate-700/30">
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-slate-400">{t("booking_service_label")}</span><span className="text-white">{booking.service_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">{t("booking_date_label")}</span><span className="text-white">{booking.date}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">{t("booking_time_label")}</span><span className="text-white font-mono">{booking.start_time} - {booking.end_time}</span></div>
                </div>
              </div>
              <Button variant="outline" className="mt-4 border-slate-700 text-slate-300"
                onClick={() => { setStep(0); setSelectedServices([]); setSelectedStaff(null); setSelectedDate(null); setSelectedSlot(null); setForm({ name: "", email: "", phone: "", notes: "" }); setBooking(null); }}
                data-testid="book-another-button">
                {t("booking_another")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
